import { Component, inject, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { HeaderService } from 'src/app/services/header.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { AppStateService } from 'src/app/services/app-state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SESSION_MERGE_STATUS } from 'src/app/base/companion-constants';
import { NavTabBarComponent, NavTabBarConfig, NavTabItem } from '@ui-components/nav-tab-bar/nav-tab-bar.component';

export interface ArtifactMenuItem {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  pinned?: boolean;
  group?: string;
  description?: string;
  count?: string | number;
  isComingSoon?: boolean;
  isPinned?: boolean;
}

@Component({
  selector: 'app-artifacts-bar',
  standalone: true,
  imports: [CommonModule, TranslateModule, NavTabBarComponent],
  templateUrl: './artifacts-bar.component.html',
  styleUrls: ['./artifacts-bar.component.scss']
})
export class ArtifactsBarComponent implements OnInit {
  public readonly headerService = inject(HeaderService);
  public readonly appStateService = inject(AppStateService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly SESSION_MERGE_STATUS = SESSION_MERGE_STATUS;

  appDetail: any = {};

  readonly menuItems: NavTabItem[] = [
    // Data & Logic
    { id: 'theme', label: 'DATA_MODEL', icon: 'data-model', route: 'designs/datamodel', group: 'DATA_AND_LOGIC' },
    { id: 'layout', label: 'WORKFLOW', icon: 'workflow', route: 'designs/workflow', group: 'DATA_AND_LOGIC' },
    { id: 'git', label: 'SERVICES', icon: 'database-03', route: 'technicaldesign/services', group: 'DATA_AND_LOGIC' },

    // Experience
    { id: 'share', label: 'SCREENS', icon: 'page', route: 'prototyping/screens', group: 'EXPERIENCE' },
    {
      id: 'security',
      label: 'CUSTOM_COMPONENTS',
      icon: 'file-code-01',
      route: 'prototyping/customcomponents',
      group: 'EXPERIENCE'
    },
    { id: 'code', label: 'THEME', icon: 'palette', route: 'prototyping/theme', group: 'EXPERIENCE' },
    {
      id: 'translate',
      label: 'TRANSLATION',
      icon: 'translate-01',
      route: 'technicaldesign/translation',
      group: 'EXPERIENCE'
    },

    // Governance
    {
      id: 'roles',
      label: 'ROLES_AND_PERMISSIONS',
      icon: 'roles',
      route: 'gathering/rolesandpermissions',
      group: 'GOVERNANCE'
    },

    // Integration
    {
      id: 'database',
      label: 'API',
      icon: 'integration',
      route: 'technicaldesign/integration',
      group: 'INTEGRATION'
    },

    // Intelligence
    {
      id: 'agents',
      label: 'AI_AGENTS',
      icon: 'cpu-chip-01',
      route: 'app-dashboards/agents',
      group: 'INTELLIGENCE'
    }
  ];

  // The shared bar owns pinning, drag/drop, grouping, and route sync.
  // This wrapper only supplies the artifacts-specific menu and storage key.
  readonly barConfig: NavTabBarConfig = {
    enablePinning: true,
    enableDragDrop: true,
    enableGrouping: true,
    storageKey: 'artifacts',
    overviewRoute: 'artifacts/overview',
    menuGroups: ['DATA_AND_LOGIC', 'EXPERIENCE', 'GOVERNANCE', 'INTEGRATION', 'INTELLIGENCE'],
    fullBorder: true
  };

  ngOnInit(): void {
    this.listenToAppDetail();
  }

  onActiveItemChange(item: NavTabItem | null): void {
    // Preserve the last build route so reopening the bar restores the most
    // relevant selection instead of always falling back to overview.
    this.headerService.lastArtifactRoute = item?.pinned ? item.route || null : 'build/overview';
  }

  private listenToAppDetail(): void {
    this.appStateService.DataMap.App.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Default-values - to avoid FOUC
        this.appDetail = {};

        const dataSourceInfo_App: any = this.appStateService.getDataSource({ name: 'App' });

        if (dataSourceInfo_App?.dataSourceExist && dataSourceInfo_App?.isItemsFilled) {
          const appId = this.appGlobalService.get('appId');
          this.appDetail = dataSourceInfo_App.data?.[appId];
          this.changeDetectorRef.detectChanges();
        }
      }
    });
  }
}
